/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import {ModelResponse, nullify, ResponseData} from '@dvrd/dvr-controls';
import IDate from '@dvrd/idate';
import {hasAdminLogin} from '../util/adminUtil';
import {InvoiceRequestStatus, InvoiceUploadType} from '../util/interfaces';
import {
    archiveInvoiceRequest, getInvoiceRequests, GetInvoiceRequests, invoiceRequestSetInvoice
} from '../util/requestSender';
import {BaseFields, BaseModel, BaseProps, createFieldDef, FieldDefinition, ModelFieldType} from './baseModel';
import Invoice from './invoiceModel';

interface Props extends BaseProps {
    order_id?: string;
    shipment_id?: string;
    customer_account_number?: string;
    vat_number?: string;
    kvk_number?: string;
    status?: InvoiceRequestStatus;
    upload_type?: InvoiceUploadType | null;
    upload_error?: string;
    country_code?: string;
    request_date?: string | null;
    uploaded_at?: string | null;
    firstname?: string;
    lastname?: string;
    address?: string;
    house_number?: string;
    house_number_extension?: string;
    zip_code?: string;
    city?: string;
    company_name?: string;
    account_id?: string;
    invoice_id?: string;
    invoice?: Invoice | null | any;
}

interface Fields extends BaseFields {
    order_id: string;
    shipment_id: string;
    customer_account_number: string;
    vat_number: string;
    kvk_number: string;
    status: InvoiceRequestStatus;
    upload_type: InvoiceUploadType | null;
    upload_error: string;
    country_code: string;
    request_date: string | null;
    uploaded_at: string | null;
    firstname: string;
    lastname: string;
    address: string;
    house_number: string;
    house_number_extension: string;
    zip_code: string;
    city: string;
    company_name: string;
    account_id: string;
    invoice_id: string;
    invoice: Invoice | null;
}

const fields: Fields = {
    id: 0,
    created_at: '',
    updated_at: '',
    order_id: '',
    shipment_id: '',
    customer_account_number: '',
    vat_number: '',
    kvk_number: '',
    status: InvoiceRequestStatus.INVOICE_REQUESTED,
    upload_type: null,
    upload_error: '',
    country_code: '',
    request_date: null,
    uploaded_at: null,
    firstname: '',
    lastname: '',
    address: '',
    house_number: '',
    house_number_extension: '',
    zip_code: '',
    city: '',
    company_name: '',
    account_id: '',
    invoice_id: '',
    invoice: null
};

const fieldsDefinition: { [key in keyof Omit<Fields, 'invoice'>]: FieldDefinition } = {
    id: createFieldDef(ModelFieldType.NUMBER),
    created_at: createFieldDef(ModelFieldType.STRING),
    updated_at: createFieldDef(ModelFieldType.STRING, undefined, true),
    order_id: createFieldDef(ModelFieldType.STRING),
    shipment_id: createFieldDef(ModelFieldType.STRING),
    customer_account_number: createFieldDef(ModelFieldType.STRING),
    vat_number: createFieldDef(ModelFieldType.STRING),
    kvk_number: createFieldDef(ModelFieldType.STRING),
    upload_error: createFieldDef(ModelFieldType.STRING),
    country_code: createFieldDef(ModelFieldType.STRING),
    request_date: createFieldDef(ModelFieldType.STRING, null, true),
    uploaded_at: createFieldDef(ModelFieldType.STRING, null, true),
    account_id: createFieldDef(ModelFieldType.STRING),
    invoice_id: createFieldDef(ModelFieldType.STRING),
    status: createFieldDef(ModelFieldType.STRING, InvoiceRequestStatus.INVOICE_REQUESTED),
    upload_type: createFieldDef(ModelFieldType.STRING, null, true),
    firstname: createFieldDef(ModelFieldType.STRING),
    lastname: createFieldDef(ModelFieldType.STRING),
    address: createFieldDef(ModelFieldType.STRING),
    house_number: createFieldDef(ModelFieldType.STRING),
    house_number_extension: createFieldDef(ModelFieldType.STRING),
    zip_code: createFieldDef(ModelFieldType.STRING),
    city: createFieldDef(ModelFieldType.STRING),
    company_name: createFieldDef(ModelFieldType.STRING)
};

const ignoreProps: Array<keyof Props> = [];

export default class BolInvoiceRequest extends BaseModel<Props, Fields> {
    static get = (id: string, callback: ModelResponse<BolInvoiceRequest>) => {

    };

    static getAll = (params: GetInvoiceRequests, callback: ModelResponse<Array<BolInvoiceRequest>>) => {
        return getInvoiceRequests({
            data: params, callback: (data: ResponseData) => {
                const requests: Array<BolInvoiceRequest> = data.success ?
                    data.invoice_requests.map((req: any) => new BolInvoiceRequest(req)) : [];
                callback(requests, data.success, data);
            }
        });
    };

    constructor(props?: Props) {
        super(ignoreProps, fieldsDefinition, fields, props);
        this.build(props);
    }

    get id(): number {
        return this.fields.id as number;
    }

    get orderId(): string {
        return this.fields.order_id;
    }

    set orderId(order_id: string) {
        this.fields.order_id = order_id;
    }

    get shipmentId(): string {
        return this.fields.shipment_id;
    }

    set shipmentId(shipment_id: string) {
        this.fields.shipment_id = shipment_id;
    }

    get customerAccountNumber(): string {
        return this.fields.customer_account_number;
    }

    set customerAccountNumber(customer_account_number: string) {
        this.fields.customer_account_number = customer_account_number;
    }

    get vatNumber(): string {
        return this.fields.vat_number;
    }

    set vatNumber(vat_number: string) {
        this.fields.vat_number = vat_number;
    }

    get kvkNumber(): string {
        return this.fields.kvk_number;
    }

    set kvkNumber(kvk_number: string) {
        this.fields.kvk_number = kvk_number;
    }

    get status(): InvoiceRequestStatus {
        return this.fields.status;
    }

    set status(status: InvoiceRequestStatus) {
        this.fields.status = status;
    }

    get translatedStatus(): string {
        const {status, uploadError, uploadType} = this;
        if (status === InvoiceRequestStatus.INVOICE_REQUESTED) {
            if (!this.invoice) return 'Oude order, factuur niet in Bolmate, handmatige actie vereist';
            return 'Open';
        } else if (status === InvoiceRequestStatus.UPLOADING) return 'Wordt geüpload';
        else if (status === InvoiceRequestStatus.INVOICE_INCORRECT ||
            status === InvoiceRequestStatus.INVOICE_VIRUS_DETECTED) {
            if (uploadError.startsWith('Shipment not found'))
                return 'Upload mislukt. Bol API error. Probleem is bekend en doorgegeven.';
            return 'Upload mislukt. Bol API timeout. Probeer opnieuw.';
        } else {
            let label = 'Geüpload';
            if (uploadType === InvoiceUploadType.PROACTIVE) label += ' via proactief';
            else if (uploadType === InvoiceUploadType.AUTO_RESPOND) label += ' via auto reactie';
            else if (uploadType === InvoiceUploadType.MANUAL) label += ' via handmatig';
            return label;
        }
    }

    get uploadType(): InvoiceUploadType | null {
        return this.fields.upload_type;
    }

    set uploadType(upload_type: InvoiceUploadType | null) {
        this.fields.upload_type = upload_type;
    }

    get uploadError(): string {
        return this.fields.upload_error;
    }

    set uploadError(upload_error: string) {
        this.fields.upload_error = upload_error;
    }

    get accountId(): string {
        return this.fields.account_id;
    }

    set accountId(account_id: string) {
        this.fields.account_id = account_id;
    }

    get invoiceId(): string {
        return this.fields.invoice_id;
    }

    set invoiceId(invoice_id: string) {
        this.fields.invoice_id = invoice_id;
    }

    get countryCode(): string {
        return this.fields.country_code.toUpperCase();
    }

    get requestDate(): IDate | null {
        if (!this.fields.request_date) return null;
        return new IDate(this.fields.request_date);
    }

    get invoice(): Invoice | null {
        return this.fields.invoice;
    }

    get uploadedAt(): IDate | null {
        if (!this.fields.uploaded_at) return null;
        return new IDate(this.fields.uploaded_at);
    }

    get firstname(): string {
        return this.fields.firstname;
    }

    get lastname(): string {
        return this.fields.lastname;
    }

    get address(): string {
        return this.fields.address;
    }

    get houseNumber(): string {
        return this.fields.house_number;
    }

    get houseNumberExtension(): string {
        return this.fields.house_number_extension;
    }

    get zipCode(): string {
        return this.fields.zip_code;
    }

    get city(): string {
        return this.fields.city;
    }

    get companyName(): string {
        return this.fields.company_name;
    }

    isRetryable = (asAdmin: boolean = false): boolean => {
        const {status, uploadError} = this;
        if (!this.invoice) return false;
        else if (status === InvoiceRequestStatus.UPLOADING && hasAdminLogin()) return true;
        else if (status === InvoiceRequestStatus.INVOICE_REQUESTED) return true;
        else if (status === InvoiceRequestStatus.INVOICE_VIRUS_DETECTED) return false;
        else if (status === InvoiceRequestStatus.INVOICE_INCORRECT)
            return !uploadError.startsWith('Shipment not found');
        return asAdmin;
    };

    archive = (callback: ModelResponse<BolInvoiceRequest>) => {
        archiveInvoiceRequest({
            data: {
                account_id: this.accountId, customer_account_number: nullify(this.customerAccountNumber),
                kvk_number: nullify(this.kvkNumber), vat_number: nullify(this.vatNumber), order_id: this.orderId,
                request_date: this.requestDate?.format() ?? null, shipment_id: this.shipmentId
            }, callback: (data: ResponseData) => {
                if (data.success) this.build(data.invoice_request);
                callback(this, data.success, data);
            }
        });
    };

    setInvoice = (invoice: Invoice, callback: ModelResponse<BolInvoiceRequest>) => {
        return invoiceRequestSetInvoice({
            data: {invoice_request_id: this.id, invoice_id: invoice.id, account_id: this.accountId},
            callback: (data: ResponseData) => {
                if (data.success) this.build(data.invoice_request);
                callback(this, data.success, data);
            }
        });
    };

    protected build = (props?: Props) => {
        this.constructFields(props);
        if (props) {
            if (props.invoice) {
                this.fields.invoice = props.invoice instanceof Invoice ? props.invoice : new Invoice(props.invoice);
            }
        }
    };
}